import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { adminStatsAPI } from '../../utils/api';
import { toast } from '../../hooks/use-toast';
import RedemptionTable from './RedemptionTable';

const RedemptionDetailModal = ({ open, onClose, storeId, storeName, date, start, end, dayLabel }) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, per_page: 15, total: 0 });

  const title = dayLabel
    ? `Redenciones: ${storeName} — ${dayLabel}`
    : `Redenciones: ${storeName}`;

  useEffect(() => {
    if (!open || !storeId) return;
    setPage(1);
  }, [open, storeId, date, start, end]);

  useEffect(() => {
    if (!open || !storeId) return;
    let cancelled = false;
    setLoading(true);
    const params = { storeId, page, limit: 15 };
    if (date) {
      params.date = date;
    } else {
      if (start) params.start = start;
      if (end) params.end = end;
    }
    adminStatsAPI
      .getRedemptionsDetail(params)
      .then((res) => {
        if (cancelled) return;
        setList(res.data || []);
        if (res.pagination) setPagination(res.pagination);
      })
      .catch((err) => {
        if (!cancelled) {
          toast({ title: 'Error', description: err.message || 'No se pudo cargar el detalle', variant: 'destructive' });
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, storeId, date, start, end, page]);

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} aria-hidden="true" />
      <div className="fixed right-0 top-0 bottom-0 w-full max-w-5xl bg-white border-l border-gray-200 z-50 flex flex-col shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg bg-gray-100 border border-gray-200 text-gray-600 hover:bg-gray-200 hover:text-gray-900 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto px-6 py-4 bg-[#F5F5F7]">
          {loading ? (
            <div className="text-center py-12 text-gray-500">Cargando...</div>
          ) : list.length === 0 ? (
            <div className="text-center py-12 text-gray-500">No hay redenciones en este rango</div>
          ) : (
            <>
              <RedemptionTable items={list} />
              {pagination.last_page > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
                  <span className="text-gray-500 text-sm">
                    {((pagination.current_page - 1) * pagination.per_page) + 1}–
                    {Math.min(pagination.current_page * pagination.per_page, pagination.total)} de {pagination.total}
                  </span>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setPage((p) => p - 1)}
                      disabled={page <= 1}
                      className="p-2 rounded-lg bg-white border border-gray-200 text-gray-700 disabled:opacity-40 hover:bg-gray-100 transition-colors"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setPage((p) => p + 1)}
                      disabled={page >= pagination.last_page}
                      className="p-2 rounded-lg bg-white border border-gray-200 text-gray-700 disabled:opacity-40 hover:bg-gray-100 transition-colors"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default RedemptionDetailModal;
